"use client"

import { useState, useRef } from "react"
import Image from "next/image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { StoreBanner } from "../../types/banner"

type BannerSlideProps = {
  banner: StoreBanner
  isFirst: boolean
}

export default function BannerSlide({ banner, isFirst }: BannerSlideProps) {
  const [videoError, setVideoError] = useState(false)
  const [isPlaying, setIsPlaying] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)

  const togglePlayPause = () => {
    if (!videoRef.current) return
    if (videoRef.current.paused) {
      videoRef.current.play()
      setIsPlaying(true)
    } else {
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }

  const renderMedia = () => {
    if (banner.media_type === "video" && banner.video_url && !videoError) {
      return (
        <div className="w-full max-w-screen-2xl mx-auto relative">
          <video
            ref={videoRef}
            src={banner.video_url}
            poster={banner.video_thumbnail_url ?? undefined}
            autoPlay
            muted
            loop
            playsInline
            onError={() => setVideoError(true)}
            className="w-full h-auto hidden md:block"
          />
          {banner.video_thumbnail_url && (
            <Image
              src={banner.video_thumbnail_url}
              alt={banner.title}
              width={768}
              height={512}
              sizes="100vw"
              className="w-full h-auto block md:hidden"
            />
          )}
          <button
            onClick={togglePlayPause}
            aria-label={isPlaying ? "Pause banner video" : "Play banner video"}
            className="absolute bottom-4 right-4 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-white hidden md:flex items-center justify-center"
          >
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <rect x="3" y="2" width="4" height="12" rx="1" />
                <rect x="9" y="2" width="4" height="12" rx="1" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M4 2.5v11l9-5.5z" />
              </svg>
            )}
          </button>
        </div>
      )
    }

    // Image banner (or video fallback)
    const imgUrl = (banner.media_type === "video" && videoError && banner.video_thumbnail_url)
      ? banner.video_thumbnail_url
      : banner.image_url

    return (
      <div className="w-full max-w-screen-2xl mx-auto">
        <Image
          src={imgUrl}
          alt={banner.title}
          width={1920}
          height={640}
          sizes="(max-width: 1536px) 100vw, 1536px"
          priority={isFirst}
          className={`w-full h-auto ${banner.mobile_image_url ? "hidden md:block" : "block"}`}
        />
        {banner.mobile_image_url && (
          <Image
            src={banner.mobile_image_url}
            alt={banner.title}
            width={768}
            height={512}
            sizes="100vw"
            priority={isFirst}
            className="w-full h-auto block md:hidden"
          />
        )}
      </div>
    )
  }

  if (banner.cta_link) {
    return (
      <LocalizedClientLink href={banner.cta_link} className="block w-full flex-shrink-0 snap-start">
        {renderMedia()}
      </LocalizedClientLink>
    )
  }

  return (
    <div className="w-full flex-shrink-0 snap-start">
      {renderMedia()}
    </div>
  )
}
